import { useEffect } from "react";
import { useQueryClient, type QueryClient, type QueryKey } from "@tanstack/react-query";
import { isStaleCursor } from "../api/content";

/**
 * The `retry` option for wall lists: a stale cursor (INVALID_FIELD) is never retried, so the restart
 * below happens at once; any other error follows the client's default (react-query: 3 retries).
 */
export function retryUnlessStaleCursor(qc: QueryClient) {
  const base = qc.getDefaultOptions().queries?.retry ?? 3;
  return (failureCount: number, error: Error): boolean => {
    if (isStaleCursor(error)) return false;
    if (typeof base === "function") return base(failureCount, error);
    if (typeof base === "boolean") return base;
    return failureCount < base;
  };
}

/**
 * room-stc rejects a cursor minted before the unified-wall cutover (422 INVALID_FIELD, spec 3a).
 * When a next-page fetch fails that way, drop the cached pages and load the first page once. The
 * first page carries no cursor, so a second INVALID_FIELD is the ordinary error state, not a loop.
 */
export function useRestartOnStaleCursor(query: { isFetchNextPageError: boolean; error: unknown }, queryKey: QueryKey) {
  const qc = useQueryClient();
  const stale = query.isFetchNextPageError && isStaleCursor(query.error);
  const keyJson = JSON.stringify(queryKey);
  useEffect(() => {
    if (stale) void qc.resetQueries({ queryKey: JSON.parse(keyJson) as QueryKey, exact: true });
  }, [stale, qc, keyJson]);
}
